/**
 * Canonical serialization of a workflow graph — the basis of dirty tracking on
 * WorkflowDetail, and the shape sent to the server.
 *
 * Every other detail page compares `JSON.stringify(form) !== JSON.stringify(original)`
 * (AgentDetail.jsx's fieldsChanged). That CANNOT work for a canvas: reactflow owns the node
 * objects and mutates them constantly, adding `selected` on click, `dragging` mid-drag,
 * `measured`/`width`/`height` after layout, and `positionAbsolute` alongside `position` —
 * none of which the user changed. A naive compare marks the form dirty on a pan, a click,
 * or the first render after mount, so Save lights up for a graph nobody edited.
 *
 * So: keep only the four fields that are actually the document, sort so key and element
 * order can never matter, and round positions — a drag emits sub-pixel floats continuously,
 * while a real move is always more than a pixel.
 */

/**
 * The graph a brand-new workflow starts from.
 *
 * NOT empty: services/workflows/graph.js's validateGraph requires exactly one `input` node
 * and at least one terminal, so a blank canvas is a workflow that can never be saved. This
 * is the smallest graph that passes — and it has no `tool` node, so it also clears the
 * model hook's "a tool node needs an agent" check before an agent has been picked.
 *
 * Lives here rather than in the editor because the new-workflow dialog creates the row
 * before the editor ever mounts.
 */
export const STARTER_GRAPH = {
  nodes: [
    { id: 'start', type: 'input', position: { x: 40, y: 120 }, data: { label: 'Start' } },
    { id: 'finish', type: 'output', position: { x: 420, y: 120 }, data: { label: 'Output' } },
  ],
  edges: [{ id: 'e_start_next', source: 'start', target: 'finish', sourceHandle: 'next' }],
};

/** Stable key order, recursively — two objects that differ only in insertion order must
 *  serialize identically. */
function sortKeys(value) {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value === null || typeof value !== 'object') return value;
  return Object.keys(value)
    .sort()
    .reduce((acc, key) => {
      acc[key] = sortKeys(value[key]);
      return acc;
    }, {});
}

/** Integer pixels. Rounding is what makes "the user panned" and "the user nudged a node"
 *  distinguishable; reactflow writes fractional positions on every animation frame. */
function roundPosition(position) {
  return {
    x: Math.round((position && position.x) || 0),
    y: Math.round((position && position.y) || 0),
  };
}

const byId = (a, b) => String(a.id).localeCompare(String(b.id));

/** The document form of a node: id, type, position, config. Nothing reactflow adds. */
export function canonicalNode(node) {
  return {
    id: String(node.id),
    type: node.type || 'default',
    position: roundPosition(node.position),
    data: sortKeys(node.data || {}),
  };
}

/** The document form of an edge. `sourceHandle` defaults to 'next' to match
 *  services/workflows/graph.js's nextNodeId, so an edge drawn from a node's only handle
 *  compares equal whether reactflow recorded null or the explicit name. */
export function canonicalEdge(edge) {
  return {
    id: String(edge.id),
    source: String(edge.source),
    target: String(edge.target),
    sourceHandle: edge.sourceHandle || 'next',
  };
}

/** The graph as the server stores it: `{nodes, edges}`, canonically ordered. */
export function canonicalGraph(nodes = [], edges = []) {
  return {
    nodes: [...nodes].map(canonicalNode).sort(byId),
    edges: [...edges].map(canonicalEdge).sort(byId),
  };
}

/** A stable string for equality checks — this is what dirty tracking compares. */
export function canonicalizeGraph(nodes = [], edges = []) {
  return JSON.stringify(canonicalGraph(nodes, edges));
}

/** True when the two graphs differ in any way the user would call an edit. */
export function graphChanged(a, b) {
  return canonicalizeGraph(a.nodes, a.edges) !== canonicalizeGraph(b.nodes, b.edges);
}

/** Parses a stored graph_json column into `{nodes, edges}`, never throwing — a new
 *  workflow has null here, and a corrupt value should open an empty canvas the user can
 *  fix rather than a blank error screen. */
export function parseGraphJson(raw) {
  if (!raw) return { nodes: [], edges: [] };
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return {
      nodes: Array.isArray(parsed?.nodes) ? parsed.nodes : [],
      edges: Array.isArray(parsed?.edges) ? parsed.edges : [],
    };
  } catch {
    return { nodes: [], edges: [] };
  }
}
